/*
 *   Copyright (c) 2026 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import React from "react";

export interface RemovedItem {
  costumeType: string;
  costumeFeature: string;
  costumeAttribute: string;
  costumeValue: string;
  costumeTypeName: string | null;
  costumeFeatureName: string | null;
  costumeAttributeName: string | null;
  costumeValueName: string | null;
  paidAmount?: number;
  paymentStatus?: string;
  isBase?: boolean;
  isPaid?: boolean;
  _id?: string;
}

export interface ExpiredItemsRemovedContent {
  removedItems: RemovedItem[];
  removedCostumeValueIds: string[];
  totalReducedAmount: number;
  previousTotalAmount: number;
  newTotalAmount: number;
  amount?: number | null;
}

export const removedItemLabel = (item: RemovedItem): string => {
  if (!item) return "Removed option";
  const path = [item.costumeFeatureName, item.costumeAttributeName]
    .filter(Boolean)
    .join(" › ");
  const option = item.costumeValueName ?? "Removed option";
  return path ? `${path}: ${option}` : option;
};

export const formatExpiredItemsTotal = (actionContent: any): string => {
  const formatNum = (num: any): string => {
    if (num === null || num === undefined || isNaN(Number(num))) {
      return "0";
    }
    return Number(num).toLocaleString("en-US");
  };

  const prev = formatNum(actionContent?.previousTotalAmount);
  const next = formatNum(actionContent?.newTotalAmount);
  const reducedRaw =
    actionContent?.totalReducedAmount !== undefined &&
    actionContent?.totalReducedAmount !== null
      ? actionContent.totalReducedAmount
      : Number(actionContent?.previousTotalAmount ?? 0) -
        Number(actionContent?.newTotalAmount ?? 0);
  const diff = formatNum(Math.abs(Number(reducedRaw)));

  return `${prev} → ${next} (−${diff})`;
};

export const renderRemovedItems = (
  removedItems: RemovedItem[] | undefined | null
): React.ReactNode => {
  if (
    !removedItems ||
    !Array.isArray(removedItems) ||
    removedItems.length === 0
  ) {
    return "Removed option";
  }
  if (removedItems.length === 1) {
    return removedItemLabel(removedItems[0]);
  }
  return (
    <>
      {removedItems.map((rItem: RemovedItem, idx: number) => (
        <span key={idx} style={{ display: "block" }}>
          {removedItemLabel(rItem)}
        </span>
      ))}
    </>
  );
};

export const appendExpiredItemsRemovedData = (
  data: any[],
  item: any,
  appCardConfig: any,
  startIndex: number
): number => {
  if (item?.action === "expired-items-removed") {
    const removedItems = item?.actionContent?.removedItems;
    data.push({
      key: startIndex++,
      title: appCardConfig?.journey?.removed ?? "Removed",
      subTitle: renderRemovedItems(removedItems),
    });

    if (
      item?.actionContent?.previousTotalAmount !== undefined ||
      item?.actionContent?.newTotalAmount !== undefined ||
      item?.actionContent?.totalReducedAmount !== undefined
    ) {
      data.push({
        key: startIndex++,
        title: appCardConfig?.journey?.total ?? "Total",
        subTitle: formatExpiredItemsTotal(item?.actionContent),
      });
    }
  }
  return startIndex;
};
